import { useContext } from 'react';
import YouTube from '@mui/icons-material/YouTube';
import Instagram from '@mui/icons-material/Instagram';
import Facebook from '@mui/icons-material/Facebook';
import Twitter from '@mui/icons-material/Twitter';
import {
  Container, Button, Grid, useTheme, useMediaQuery,
} from '@mui/material';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import type { ITheme } from '../../..';
import { AppConstantsContext, COMPANY_URL } from '../../..';
import useLocaleUrl from '../../../hooks/useLocaleUrl';

const useStyles = makeStyles()((theme: ITheme) => ({
  footer: {
    overflow: 'hidden',
  },
  copyrightCol: {
    textAlign: 'left',
    lineHeight: '36px',
    '@media screen and (max-width:767px)': {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      textAlign: 'center',
    },
  },
  headingRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('md')]: {
      alignItems: 'center',
    },
  },
  headingRowSocial: {
    alignItems: 'center',
  },
  socials: { color: 'white', margin: 4 },
  createdBy: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    width: '100%',
    textAlign: 'center',
  },
  copyright: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  companyLogo: { width: 100, marginTop: 20 },
  linkItem: {
    textAlign: 'left',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'flex-start',
      width: '100%',
    },
  },
}));

const CompanyCopyRight = () => {
  const { classes } = useStyles();

  return (
    <div className={classes.copyright}>
      <img
        width="100"
        height="107"
        className={classes.companyLogo}
        src="/img/global/klangio_company_logo.png"
        alt="Company Logo"
      />
      <span>
        <br />
        {`© Copyright ${new Date().getFullYear()}`}
        {' '}
        <a style={{ color: '#fff' }} href="https://klang.io">
          Klangio GmbH
        </a>
        .
        <br />
      </span>
      All rights reserved.
    </div>
  );
};

export interface FooterLink {
  title: string;
  url: string;
  tracking_id: string;
}

const AdvancedFooter = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles();
  const { supportMail, supportSubject, helpLink, isMusehub } = useContext(AppConstantsContext);
  const localeUrl = useLocaleUrl();
  const isBelowXS = useMediaQuery(theme.breakpoints.down('md'));

  if (isMusehub) return null;

  const companyLinks = [
    {
      url: `${COMPANY_URL}/press/`,
      title: t('footer.press'),
      tracking_id: 'btn_footer_press',
    },
    {
      url: `${COMPANY_URL}/blog/`,
      title: t('footer.blog'),
      tracking_id: 'btn_footer_blog',
    },
  ];
  const communityLinks = [
    { url: helpLink, title: t('footer.help'), tracking_id: 'btn_footer_help' },
    {
      url: `${COMPANY_URL}/gift-cards`,
      title: t('footer.gift-cards'),
      tracking_id: 'btn_footer_gift_cards',
    },
    { url: localeUrl('/redeem'), title: t('footer.redeem'), tracking_id: 'btn_footer_redeem' },
    {
      url: `${COMPANY_URL}/updates/`,
      title: t('footer.updates'),
      tracking_id: 'btn_footer_updates',
    },
  ].filter((value) => value.title !== undefined);

  const otherLinks = [
    {
      url: `${COMPANY_URL}/privacy/`,
      title: t('footer.privacy'),
      tracking_id: 'btn_footer_privacy',
    },
    { url: `${COMPANY_URL}/terms/`, title: t('footer.terms'), tracking_id: 'btn_footer_terms' },
    {
      url: `${COMPANY_URL}/imprint/`,
      title: t('footer.imprint'),
      tracking_id: 'btn_footer_imprint',
    },
  ];
  const renderLink = (data: FooterLink) => (
    <Button
      id={data.tracking_id}
      className={classes.linkItem}
      key={data.title}
      color="inherit"
      href={data.url}
    >
      {data.title}
    </Button>
  );

  const innerGridXS = 12;
  const innerGridSM = 4;
  const innerGridSMSocial = 12;
  const innerGridMD = 3;
  return (
    <footer className={classes.footer}>
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          {!isBelowXS && (
            <Grid item xs={12} sm={12} md={3} className={classes.copyrightCol}>
              <CompanyCopyRight />
            </Grid>
          )}

          <Grid container spacing={3} item md={9}>
            <Grid item xs={innerGridXS} sm={innerGridSM} md={innerGridMD}>
              <div className={classes.headingRow}>
                <h3>Company</h3>
                {companyLinks && companyLinks.map(renderLink)}
              </div>
            </Grid>
            <Grid item xs={innerGridXS} sm={innerGridSM} md={innerGridMD}>
              <div className={classes.headingRow}>
                <h3>Community</h3>
                {communityLinks && communityLinks.map(renderLink)}
              </div>
            </Grid>
            <Grid item xs={innerGridXS} sm={innerGridSM} md={innerGridMD}>
              <div className={classes.headingRow}>
                <h3>Other</h3>
                {otherLinks && otherLinks.map(renderLink)}
              </div>
            </Grid>
            <Grid item xs={innerGridXS} sm={innerGridSMSocial} md={innerGridMD}>
              <div className={classNames(classes.headingRow, classes.headingRowSocial)}>
                <h3>Socials</h3>
                <div>
                  <a
                    id="btn_footer_youtube"
                    className={classes.socials}
                    href="https://www.youtube.com/c/Klangio"
                    aria-label="YouTube link"
                  >
                    <YouTube />
                  </a>
                  <a
                    id="btn_footer_instagram"
                    className={classes.socials}
                    href="https://www.instagram.com/klangio/"
                    aria-label="Instagram link"
                  >
                    <Instagram />
                  </a>
                  <a
                    id="btn_footer_facebook"
                    className={classes.socials}
                    href="https://www.facebook.com/klangio/"
                    aria-label="Facebook link"
                  >
                    <Facebook />
                  </a>
                  <a
                    id="btn_footer_twitter"
                    className={classes.socials}
                    href="https://twitter.com/Klangio"
                    aria-label="Twitter link"
                  >
                    <Twitter />
                  </a>
                </div>
                {supportMail && supportSubject && (
                  <>
                    <h4>Support</h4>
                    <Button
                      id="btn_footer_support"
                      size="small"
                      color="inherit"
                      href={`mailto:${supportMail}?subject=${supportSubject}`}
                    >
                      {supportMail}
                    </Button>
                  </>
                )}
              </div>
            </Grid>
          </Grid>
          {isBelowXS && (
            <Grid item xs={12} sm={12} md={3} className={classes.copyrightCol}>
              <CompanyCopyRight />
            </Grid>
          )}
          <p className={classes.createdBy}>Created with ❤️ and 🎵 in Karlsruhe</p>
        </Grid>
      </Container>
    </footer>
  );
};

export default AdvancedFooter;
